import { Link } from "@tanstack/react-router";
import { Check, Search } from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "@/auth/client";
import { formatCurrency } from "@/components/deal-metrics/shared";
import { PageHeader } from "@/components/shared/page-header";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface OwnerKpi {
  position: number;
  label: string;
  value: string;
}

interface OwnerPto {
  year: number;
  issued: number;
  used: number;
}

interface OwnerBenefits {
  healthInsurance: boolean;
  shortTermDisability: boolean;
  longTermDisability: boolean;
  appleComputer: boolean;
  retirement401k: boolean;
}

interface OwnerQuarterlyBonuses {
  q1: number;
  q2: number;
  q3: number;
  q4: number;
}

interface OwnerSecurityPage {
  label: string;
  path: string | null;
}

interface OwnerSecurity {
  hasAccount: boolean;
  authRole: string | null;
  banned: boolean;
  pages: OwnerSecurityPage[];
}

interface HROwner {
  id: string;
  hubspotOwnerId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  role: string | null;
  image: string | null;
  baseSalary: number | null;
  annualBonusPercent: number | null;
  equityPercent: number | null;
  hireDate: string | null;
  team: string | null;
  managerId: string | null;
  managerName: string | null;
  kpis: OwnerKpi[];
  pto: OwnerPto[];
  benefits: OwnerBenefits;
  quarterlyBonuses: OwnerQuarterlyBonuses;
  security: OwnerSecurity;
}

type FlashKey = string; // `${ownerId}-${position}-label` | `${ownerId}-${position}-value`

export function HRPage() {
  const { data: session } = useSession();
  const isAdmin = (session?.user as { role?: string })?.role === "admin";

  const [owners, setOwners] = useState<HROwner[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [flashing, setFlashing] = useState<Set<FlashKey>>(new Set());

  // Local edits buffer: ownerId → kpis array
  const [edits, setEdits] = useState<Map<string, OwnerKpi[]>>(new Map());
  const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // PTO edits buffer: `${ownerId}-${year}` → issued (the only HR-editable PTO field;
  // "used" is computed from approved requests and shown read-only)
  const [ptoEdits, setPtoEdits] = useState<Map<string, string>>(new Map());
  const ptoSaveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const currentYear = new Date().getFullYear();

  // Benefits edits buffer: ownerId → OwnerBenefits
  const [benefitsEdits, setBenefitsEdits] = useState<Map<string, OwnerBenefits>>(new Map());

  const fetchOwners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/hr");
      if (res.ok) {
        const data: HROwner[] = await res.json();
        setOwners(data);
        // Seed KPI edits
        const m = new Map<string, OwnerKpi[]>();
        for (const o of data) {
          m.set(o.id, o.kpis.length > 0 ? [...o.kpis] : []);
        }
        setEdits(m);
        // Seed PTO edits
        const pm = new Map<string, string>();
        for (const o of data) {
          const row = o.pto.find((p) => p.year === currentYear);
          pm.set(`${o.id}-${currentYear}`, row ? String(row.issued) : "");
        }
        setPtoEdits(pm);
        // Seed benefits edits
        const bm = new Map<string, OwnerBenefits>();
        for (const o of data) {
          bm.set(o.id, { ...o.benefits });
        }
        setBenefitsEdits(bm);
      }
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  function getKpis(ownerId: string): OwnerKpi[] {
    return edits.get(ownerId) ?? [];
  }

  function getKpi(ownerId: string, position: number): OwnerKpi {
    const kpis = getKpis(ownerId);
    return kpis.find((k) => k.position === position) ?? { position, label: "", value: "" };
  }

  function updateKpi(ownerId: string, position: number, field: "label" | "value", val: string) {
    setEdits((prev) => {
      const next = new Map(prev);
      const kpis = [...(next.get(ownerId) ?? [])];
      const idx = kpis.findIndex((k) => k.position === position);
      if (idx >= 0) {
        const existing = kpis[idx]!;
        kpis[idx] = {
          position: existing.position,
          label: field === "label" ? val : existing.label,
          value: field === "value" ? val : existing.value,
        };
      } else {
        kpis.push({
          position,
          label: field === "label" ? val : "",
          value: field === "value" ? val : "",
        });
      }
      next.set(ownerId, kpis);
      return next;
    });

    // Debounced save
    const timerKey = `${ownerId}-${position}`;
    clearTimeout(saveTimers.current.get(timerKey));
    saveTimers.current.set(
      timerKey,
      setTimeout(() => saveKpis(ownerId, position, field), 800),
    );
  }

  async function saveKpis(ownerId: string, position: number, field: "label" | "value") {
    const kpis = edits.get(ownerId) ?? [];
    const nonEmpty = kpis.filter((k) => k.label.trim() !== "" || k.value.trim() !== "");

    await fetch(`/api/hr/owners/${ownerId}/kpis`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kpis: nonEmpty }),
    });

    const flashKey: FlashKey = `${ownerId}-${position}-${field}`;
    setFlashing((prev) => {
      const next = new Set(prev);
      next.add(flashKey);
      return next;
    });
    setTimeout(() => {
      setFlashing((prev) => {
        const next = new Set(prev);
        next.delete(flashKey);
        return next;
      });
    }, 1500);
  }

  function updatePto(ownerId: string, val: string) {
    const key = `${ownerId}-${currentYear}`;
    setPtoEdits((prev) => new Map(prev).set(key, val));
    clearTimeout(ptoSaveTimers.current.get(key));
    ptoSaveTimers.current.set(
      key,
      setTimeout(() => savePto(ownerId), 800),
    );
  }

  async function savePto(ownerId: string) {
    const key = `${ownerId}-${currentYear}`;
    const issued = Number(ptoEdits.get(key)) || 0;
    await fetch(`/api/hr/owners/${ownerId}/pto`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ year: currentYear, issued }),
    });
    const flashKey: FlashKey = `pto-${ownerId}`;
    setFlashing((prev) => new Set(prev).add(flashKey));
    setTimeout(() => {
      setFlashing((prev) => {
        const next = new Set(prev);
        next.delete(flashKey);
        return next;
      });
    }, 1500);
  }

  async function toggleBenefit(ownerId: string, field: keyof OwnerBenefits) {
    const cur = benefitsEdits.get(ownerId) ?? {
      healthInsurance: false,
      shortTermDisability: false,
      longTermDisability: false,
      appleComputer: false,
      retirement401k: false,
    };
    const updated = { ...cur, [field]: !cur[field] };
    setBenefitsEdits((prev) => new Map(prev).set(ownerId, updated));
    await fetch(`/api/hr/owners/${ownerId}/benefits`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updated),
    });
    const flashKey: FlashKey = `ben-${ownerId}`;
    setFlashing((prev) => new Set(prev).add(flashKey));
    setTimeout(() => {
      setFlashing((prev) => {
        const next = new Set(prev);
        next.delete(flashKey);
        return next;
      });
    }, 1000);
  }

  const TEAM_ORDER = ["Development", "Operations", "Marketing", "Consultants"];

  const filtered = owners
    .filter((o) => {
      const q = search.toLowerCase();
      const name = `${o.firstName} ${o.lastName}`.toLowerCase();
      return (
        name.includes(q) ||
        (o.role ?? "").toLowerCase().includes(q) ||
        (o.managerName ?? "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const ai = TEAM_ORDER.indexOf(a.team ?? "");
      const bi = TEAM_ORDER.indexOf(b.team ?? "");
      const teamDiff = (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      if (teamDiff !== 0) return teamDiff;
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });

  function Initials({ owner }: { owner: HROwner }) {
    const initials = `${owner.firstName[0] ?? ""}${owner.lastName[0] ?? ""}`.toUpperCase();
    return (
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
        {initials}
      </div>
    );
  }

  function KpiCell({ ownerId, position }: { ownerId: string; position: number }) {
    const kpi = getKpi(ownerId, position);
    const labelFlash = flashing.has(`${ownerId}-${position}-label`);
    const valueFlash = flashing.has(`${ownerId}-${position}-value`);

    if (!isAdmin) {
      if (!kpi.label && !kpi.value) {
        return <span className="text-muted-foreground text-xs">—</span>;
      }
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] text-muted-foreground leading-none">{kpi.label}</span>
          <span className="font-semibold text-sm">{kpi.value}</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1 min-w-[120px]">
        <input
          type="text"
          placeholder="Label"
          value={kpi.label}
          onChange={(e) => updateKpi(ownerId, position, "label", e.target.value)}
          className={cn(
            "w-full text-[11px] text-muted-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-0 py-0.5 transition-colors",
            labelFlash && "border-green-500 text-green-700",
          )}
        />
        <div className="relative">
          <input
            type="text"
            placeholder="Value"
            value={kpi.value}
            onChange={(e) => updateKpi(ownerId, position, "value", e.target.value)}
            className={cn(
              "w-full text-sm font-semibold bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-0 py-0.5 transition-colors",
              valueFlash && "border-green-500 text-green-700",
            )}
          />
          {valueFlash && <Check className="absolute right-0 top-1 h-3 w-3 text-green-500" />}
        </div>
      </div>
    );
  }

  function TeamBadge({ team }: { team: string | null }) {
    if (!team) return <span className="text-muted-foreground text-xs">—</span>;
    return (
      <span
        className={cn(
          "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
          team === "Marketing" &&
            "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
          team === "Consultants" &&
            "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
          team === "Operations" &&
            "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
          team === "Development" &&
            "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        )}
      >
        {team}
      </span>
    );
  }

  function PtoInput({ ownerId }: { ownerId: string }) {
    const key = `${ownerId}-${currentYear}`;
    const val = ptoEdits.get(key) ?? "";
    const isFlashing = flashing.has(`pto-${ownerId}`);

    if (!isAdmin) {
      return <span className="tabular-nums">{val || "—"}</span>;
    }

    return (
      <div className="relative inline-flex items-center">
        <input
          type="number"
          min={0}
          placeholder="0"
          value={val}
          onChange={(e) => updatePto(ownerId, e.target.value)}
          className={cn(
            "w-16 text-sm tabular-nums bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-0 py-0.5 text-center transition-colors",
            isFlashing && "border-green-500 text-green-700",
          )}
        />
        {isFlashing && <Check className="absolute -right-4 top-1 h-3 w-3 text-green-500" />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Human Resources"
        description="All team members, their roles, reporting structure, and key performance indicators."
      />

      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by name, title, or manager…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <Tabs defaultValue="team">
        <TabsList>
          <TabsTrigger value="team">Team Directory</TabsTrigger>
          <TabsTrigger value="performance">Performance Tracker</TabsTrigger>
          <TabsTrigger value="pto">PTO Tracker</TabsTrigger>
          <TabsTrigger value="benefits">Salary &amp; Benefits</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="mt-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="sticky left-0 z-20 bg-muted/30 font-semibold">
                    Team Member
                  </TableHead>
                  <TableHead className="font-semibold">Job Title</TableHead>
                  <TableHead className="font-semibold">Reports To</TableHead>
                  <TableHead className="font-semibold">Team</TableHead>
                  <TableHead className="font-semibold">Hire Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      {search ? "No team members match your search." : "No team members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((owner) => (
                    <TableRow key={owner.id} className="hover:bg-muted/20">
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Initials owner={owner} />
                          <div className="flex flex-col">
                            <Link
                              to="/owner-metrics/$ownerId"
                              params={{ ownerId: owner.hubspotOwnerId ?? owner.id }}
                              className="font-medium leading-tight hover:underline"
                            >
                              {owner.firstName} {owner.lastName}
                            </Link>
                            <span className="text-xs text-muted-foreground">{owner.email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {owner.role ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {owner.managerName ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        <TeamBadge team={owner.team} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {owner.hireDate ? (
                          new Date(`${owner.hireDate}T00:00:00`).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="sticky left-0 z-20 bg-muted/30 font-semibold">
                    Team Member
                  </TableHead>
                  <TableHead className="font-semibold text-center">KPI 1</TableHead>
                  <TableHead className="font-semibold text-center">KPI 2</TableHead>
                  <TableHead className="font-semibold text-center">KPI 3</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`perf-skel-${i}`}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-10 w-28 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-10 w-28 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-10 w-28 mx-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                      {search ? "No team members match your search." : "No team members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((owner) => (
                    <TableRow key={owner.id} className="hover:bg-muted/20">
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Initials owner={owner} />
                          <div className="flex flex-col">
                            <Link
                              to="/owner-metrics/$ownerId"
                              params={{ ownerId: owner.hubspotOwnerId ?? owner.id }}
                              className="font-medium leading-tight hover:underline"
                            >
                              {owner.firstName} {owner.lastName}
                            </Link>
                            <span className="text-xs text-muted-foreground">{owner.email}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <KpiCell ownerId={owner.id} position={1} />
                      </TableCell>
                      <TableCell className="text-center">
                        <KpiCell ownerId={owner.id} position={2} />
                      </TableCell>
                      <TableCell className="text-center">
                        <KpiCell ownerId={owner.id} position={3} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {isAdmin && !loading && (
            <p className="text-xs text-muted-foreground mt-3">
              Click any KPI label or value to edit it — changes save automatically.
            </p>
          )}
        </TabsContent>

        <TabsContent value="pto" className="mt-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="sticky left-0 z-20 bg-muted/30 font-semibold">
                    Team Member
                  </TableHead>
                  <TableHead className="font-semibold">Team</TableHead>
                  <TableHead className="font-semibold text-center">
                    PTO Issued ({currentYear})
                  </TableHead>
                  <TableHead className="font-semibold text-center">
                    PTO Used ({currentYear})
                  </TableHead>
                  <TableHead className="font-semibold text-center">
                    PTO Remaining ({currentYear})
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`pto-skel-${i}`}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 mx-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                      {search ? "No team members match your search." : "No team members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((owner) => {
                    const key = `${owner.id}-${currentYear}`;
                    const issued = Number(ptoEdits.get(key)) || 0;
                    const used = owner.pto.find((p) => p.year === currentYear)?.used ?? 0;
                    const remaining = issued - used;
                    return (
                      <TableRow key={owner.id} className="hover:bg-muted/20">
                        <TableCell className="sticky left-0 z-10 bg-background">
                          <div className="flex items-center gap-3">
                            <Initials owner={owner} />
                            <div className="flex flex-col">
                              <Link
                                to="/owner-metrics/$ownerId"
                                params={{ ownerId: owner.hubspotOwnerId ?? owner.id }}
                                className="font-medium leading-tight hover:underline"
                              >
                                {owner.firstName} {owner.lastName}
                              </Link>
                              <span className="text-xs text-muted-foreground">
                                {owner.role ?? ""}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <TeamBadge team={owner.team} />
                        </TableCell>
                        <TableCell className="text-center">
                          <PtoInput ownerId={owner.id} />
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm tabular-nums text-muted-foreground">{used}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={cn(
                              "font-semibold tabular-nums text-sm",
                              remaining < 0 && "text-red-600",
                              remaining === 0 && issued > 0 && "text-muted-foreground",
                              remaining > 0 && "text-green-700 dark:text-green-400",
                            )}
                          >
                            {issued === 0 && used === 0 ? "—" : remaining}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {isAdmin && !loading && (
            <p className="text-xs text-muted-foreground mt-3">
              Enter the PTO days issued for each team member. Used and Remaining are calculated
              automatically from approved time-off requests, and match what each person sees on
              their own dashboard. Changes save automatically.
            </p>
          )}
        </TabsContent>

        <TabsContent value="benefits" className="mt-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="sticky left-0 z-20 bg-muted/30 font-semibold">
                    Team Member
                  </TableHead>
                  <TableHead className="font-semibold">Team</TableHead>
                  <TableHead className="font-semibold text-right">Base Salary</TableHead>
                  <TableHead className="font-semibold text-right">Annual Bonus %</TableHead>
                  <TableHead className="font-semibold text-right">Equity %</TableHead>
                  <TableHead className="font-semibold text-right">Q1 Bonus</TableHead>
                  <TableHead className="font-semibold text-right">Q2 Bonus</TableHead>
                  <TableHead className="font-semibold text-right">Q3 Bonus</TableHead>
                  <TableHead className="font-semibold text-right">Q4 Bonus</TableHead>
                  <TableHead className="font-semibold text-center">Health</TableHead>
                  <TableHead className="font-semibold text-center">Short Term Disability</TableHead>
                  <TableHead className="font-semibold text-center">Long Term Disability</TableHead>
                  <TableHead className="font-semibold text-center">Apple Computer</TableHead>
                  <TableHead className="font-semibold text-center">401(k)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`ben-skel-${i}`}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-24 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-16 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20 ml-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-5 rounded mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-5 rounded mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-5 rounded mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-5 rounded mx-auto" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-5 rounded mx-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="h-24 text-center text-muted-foreground">
                      {search ? "No team members match your search." : "No team members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  <Fragment>
                    {filtered.map((owner) => {
                      const b = benefitsEdits.get(owner.id) ?? {
                        healthInsurance: false,
                        shortTermDisability: false,
                        longTermDisability: false,
                        appleComputer: false,
                        retirement401k: false,
                      };
                      const flashed = flashing.has(`ben-${owner.id}`);
                      const fields: { key: keyof OwnerBenefits; label: string }[] = [
                        { key: "healthInsurance", label: "Health" },
                        { key: "shortTermDisability", label: "Short Term Disability" },
                        { key: "longTermDisability", label: "Long Term Disability" },
                        { key: "appleComputer", label: "Apple Computer" },
                        { key: "retirement401k", label: "401k" },
                      ];
                      return (
                        <TableRow key={owner.id} className="hover:bg-muted/20">
                          <TableCell className="sticky left-0 z-10 bg-background">
                            <div className="flex items-center gap-3">
                              <Initials owner={owner} />
                              <div className="flex flex-col">
                                <Link
                                  to="/owner-metrics/$ownerId"
                                  params={{ ownerId: owner.hubspotOwnerId ?? owner.id }}
                                  className="font-medium leading-tight hover:underline"
                                >
                                  {owner.firstName} {owner.lastName}
                                </Link>
                                <span className="text-xs text-muted-foreground">
                                  {owner.role ?? ""}
                                </span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <TeamBadge team={owner.team} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-medium">
                            {owner.baseSalary != null ? (
                              new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                                minimumFractionDigits: 0,
                              }).format(owner.baseSalary)
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-medium">
                            {owner.annualBonusPercent != null ? (
                              `${owner.annualBonusPercent}%`
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm font-medium">
                            {owner.equityPercent != null ? (
                              `${owner.equityPercent}%`
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          {(
                            [
                              ["q1", owner.quarterlyBonuses.q1],
                              ["q2", owner.quarterlyBonuses.q2],
                              ["q3", owner.quarterlyBonuses.q3],
                              ["q4", owner.quarterlyBonuses.q4],
                            ] as const
                          ).map(([quarter, amount]) => (
                            <TableCell
                              key={quarter}
                              className="text-right tabular-nums text-sm font-medium"
                            >
                              {amount > 0 ? (
                                formatCurrency(amount)
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          ))}
                          {fields.map(({ key }) => (
                            <TableCell key={key} className="text-center">
                              {isAdmin ? (
                                <button
                                  type="button"
                                  onClick={() => toggleBenefit(owner.id, key)}
                                  className={cn(
                                    "h-6 w-6 rounded border-2 flex items-center justify-center mx-auto transition-colors",
                                    b[key]
                                      ? "bg-primary border-primary text-primary-foreground"
                                      : "border-border bg-background hover:border-primary/50",
                                    flashed && b[key] && "border-green-500 bg-green-500",
                                  )}
                                  aria-label={`Toggle ${key}`}
                                >
                                  {b[key] && <Check className="h-3.5 w-3.5" />}
                                </button>
                              ) : (
                                <span
                                  className={cn(
                                    "text-sm",
                                    b[key] ? "text-green-600" : "text-muted-foreground",
                                  )}
                                >
                                  {b[key] ? "✓" : "—"}
                                </span>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                    <TableRow className="bg-muted/40 font-semibold border-t-2 hover:bg-muted/40">
                      <TableCell colSpan={2}>Total ({filtered.length})</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(filtered.reduce((sum, o) => sum + (o.baseSalary ?? 0), 0))}
                      </TableCell>
                      <TableCell colSpan={2} />
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(
                          filtered.reduce((sum, o) => sum + o.quarterlyBonuses.q1, 0),
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(
                          filtered.reduce((sum, o) => sum + o.quarterlyBonuses.q2, 0),
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(
                          filtered.reduce((sum, o) => sum + o.quarterlyBonuses.q3, 0),
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatCurrency(
                          filtered.reduce((sum, o) => sum + o.quarterlyBonuses.q4, 0),
                        )}
                      </TableCell>
                      <TableCell colSpan={5} />
                    </TableRow>
                  </Fragment>
                )}
              </TableBody>
            </Table>
          </div>
          {isAdmin && !loading && (
            <p className="text-xs text-muted-foreground mt-3">
              Click the checkboxes to toggle benefit enrollment — changes save instantly.
            </p>
          )}
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="sticky left-0 z-20 bg-muted/30 font-semibold">
                    Team Member
                  </TableHead>
                  <TableHead className="font-semibold">Account Status</TableHead>
                  <TableHead className="font-semibold">Pages They Can View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`sec-skel-${i}`}>
                      <TableCell className="sticky left-0 z-10 bg-background">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-8 rounded-full" />
                          <Skeleton className="h-4 w-32" />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-5 w-20 rounded-full" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-48" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                      {search ? "No team members match your search." : "No team members found."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((owner) => {
                    const sec = owner.security;
                    return (
                      <TableRow key={owner.id} className="hover:bg-muted/20">
                        <TableCell className="sticky left-0 z-10 bg-background">
                          <div className="flex items-center gap-3">
                            <Initials owner={owner} />
                            <div className="flex flex-col">
                              <Link
                                to="/owner-metrics/$ownerId"
                                params={{ ownerId: owner.hubspotOwnerId ?? owner.id }}
                                className="font-medium leading-tight hover:underline"
                              >
                                {owner.firstName} {owner.lastName}
                              </Link>
                              <span className="text-xs text-muted-foreground">{owner.email}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {sec.banned ? (
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              Suspended
                            </span>
                          ) : !sec.hasAccount ? (
                            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                              No Login Access
                            </span>
                          ) : sec.authRole === "admin" ? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                              Admin
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Employee
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {sec.pages.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {sec.pages.map((page) =>
                                page.path ? (
                                  <a
                                    key={page.label}
                                    href={page.path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-block rounded bg-muted px-1.5 py-0.5 text-xs text-brand-navy hover:underline"
                                  >
                                    {page.label}
                                  </a>
                                ) : (
                                  <span
                                    key={page.label}
                                    className="inline-block rounded bg-muted px-1.5 py-0.5 text-xs"
                                  >
                                    {page.label}
                                  </span>
                                ),
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {!loading && (
            <p className="text-xs text-muted-foreground mt-3">
              Employees can only ever see their own dashboard (Performance, PTO, Salary/Benefits,
              Performance Reviews), the Org Chart, and any team roster pages they belong to — never
              another team member's individual salary, benefits, or performance data.
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
